import { Form } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { ReferencedOperationRelationship } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ArsBoolean } from "../inputs/ArsBoolean";
import { ArsText } from "../inputs/ArsText";
import { ArsVariable } from "../inputs/ArsVariable";
import { isOperation } from "../typeGuards/isOperation";
import { NamedObjectControl } from "./NamedObjectControl";
import { ReferencedOperationRelationshipControl } from "./ReferencedOperationRelationshipControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the OperationControl component.
 */
export interface OperationControlProps {
  /** RFC 6901 JSON Pointer path to the Operation in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing Operation entities.
 *
 * This component provides:
 * - NamedObjectControl for editing name, description, and label
 * - Result pattern input
 * - Default target variable for new table operations
 * - Management of nested ReferencedOperationRelationship entities
 * - Remove button for deleting the operation
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the Operation in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the operation editor.
 */
export const OperationControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: OperationControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();

  let correctedDataPath = dataPath;
  // Find correct datapath for the operation in the analysisMethods
  if (correctedDataPath.startsWith("/operations/")) {
    let ix = -1;
    const holdingAnalysisMethod = Object.values(state.analysisMethods).find((am) => {
      const thisIx = am.operations.findIndex((op) => op.id === correctedDataPath.split("/").pop());
      ix = thisIx;
      return thisIx > -1;
    });
    /* istanbul ignore else */
    if (holdingAnalysisMethod) {
      correctedDataPath = `/analysisMethods/${holdingAnalysisMethod.id}/operations/${ix}`;
    }
  }
  // Check state settings for disableAnalysisMethodEdit
  if (state.disableAnalysisMethodEdit) disabled = true;

  const operation = getFromDataPath(correctedDataPath, state, isOperation);
  const relationships = operation?.referencedOperationRelationships ?? [];

  /**
   * Handles adding a new referenced operation relationship.
   */
  const handleAddRelationship = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && operation) {
      const relationshipCount = relationships.length;
      const newRelationship: ReferencedOperationRelationship = {
        id: crypto.randomUUID(),
        referencedOperationRole: {
          controlledTerm: "NUMERATOR",
        },
        operationId: "",
        analysisId: "",
        description: "",
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${correctedDataPath}/referencedOperationRelationships/${relationshipCount}`,
        value: newRelationship,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {operation && (
        <div className="position-absolute bottom-0 end-0 m-1">
          <Form.Text>
            <small>Id: {operation.id}</small>
          </Form.Text>
        </div>
      )}
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={correctedDataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectControl
        dataPath={correctedDataPath}
        canRename
        disabled={disabled}
      />

      <ArsText
        dataPath={`${correctedDataPath}/resultPattern`}
        id={`${idRoute}-result-pattern`}
        label="Result pattern"
        labelWidth={2}
        placeholder="Enter result pattern"
        disabled={disabled}
      />

      <ArsBoolean
        dataPath={`${correctedDataPath}/denominatorOnly`}
        id={`${idRoute}-denominator-only`}
        label="Denominator only"
        labelWidth={2}
        disabled={disabled}
      />

      <ArsVariable
        combinedDataPath={`${correctedDataPath}/defaultTargetVariable`}
        id={`${idRoute}-default-target-variable`}
        label="Default target variable"
        labelWidth={2}
        disabled={disabled}
      />

      {relationships.map((relationship, index) => (
        <ReferencedOperationRelationshipControl
          key={relationship.id}
          dataPath={`${correctedDataPath}/referencedOperationRelationships/${index}`}
          disabled={disabled}
          siblingCount={relationships.length}
        />
      ))}

      <AsyncButton
        id={`${idRoute}-add-relationship`}
        onClick={handleAddRelationship}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled}
      >
        <span className="me-2">Add relationship</span>
        <PlusCircle />
      </AsyncButton>
    </div>
  );
};

OperationControl.displayName = "OperationControl";
